import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { GlobalSearchService } from './globalsearchservice.service';
import { RuntimeConfigLoaderService } from 'runtime-config-loader';

export interface IntegrationConfig {
	id?: number;
	client_id?: string;
	channel: 'leaflink' | 'distru';
	enabled: boolean;
	test_mode: boolean;
	api_key: string;
	api_base?: string;
	company_id?: number;
	default_customer?: string;
	default_customer_id?: number;
	max_retry_attempts?: number;
	last_tested_at?: string;
	last_test_status?: string;
}

export interface CustomerMap {
	id: number;
	client_id: string;
	channel: string;
	debtorno: string;
	external_id: string;
	external_name: string;
	debtor_name?: string;
}

export interface ChannelOrder {
	orderno: string;
	debtorno: string;
	customer_name: string;
	orddate: string;
	reqdate: string;
	orderstatusid: number;
	order_channel: string;
	channel_order_ref: string;
	customerref: string;
	order_total: number;
	ll_push_status?: string;
	ll_external_id?: string;
	ll_push_error?: string;
	distru_push_status?: string;
	distru_external_id?: string;
	distru_push_error?: string;
}

@Injectable({ providedIn: 'root' })
export class ChannelordersService {
	private config: any;
	user: any = {};
	private configSavedSource = new Subject<void>();
	configSaved$ = this.configSavedSource.asObservable();

	constructor(
		private http: HttpClient,
		private globalSearchService: GlobalSearchService,
		private configSvc: RuntimeConfigLoaderService
	) {
		this.globalSearchService.configsubscription.subscribe(r => { this.config = r; });
		this.globalSearchService.user.subscribe(r => { this.user = r; });
	}

	notifyConfigSaved(): void { this.configSavedSource.next(); }

	private setEndPoint(method: string): string {
		return this.getApi() + method + this.getApiKey();
	}
	private getApi(): string { return this.config.apiServer.endpoint; }
	private getApiKey(): string {
		const pkg = this.globalSearchService.getSessionItem('package');
		return '&key=' + this.config.apiCredentials.clientId
			+ '&userid=' + this.user.user.userid
			+ (pkg ? '&package=' + pkg : '');
	}

	// ---- Config ----

	getConfig(channel: string): Observable<{ success: boolean; config: IntegrationConfig | null }> {
		return this.http.get<any>(this.setEndPoint('integration/getConfig&channel=' + channel));
	}

	saveConfig(config: IntegrationConfig): Observable<any> {
		return this.http.post(this.setEndPoint('integration/saveConfig'), config);
	}

	testConnection(channel: string): Observable<any> {
		return this.http.post(this.setEndPoint('integration/testConnection'), { channel });
	}

	// ---- Customer Mapping ----

	getCustomerMap(channel: string): Observable<{ success: boolean; data: CustomerMap[] }> {
		return this.http.get<any>(this.setEndPoint('integration/getCustomerMap&channel=' + channel));
	}

	saveCustomerMap(map: Partial<CustomerMap>): Observable<any> {
		return this.http.post(this.setEndPoint('integration/saveCustomerMap'), map);
	}

	deleteCustomerMap(id: number): Observable<any> {
		return this.http.post(this.setEndPoint('integration/deleteCustomerMap'), { id });
	}

	getExternalCustomers(channel: string): Observable<any> {
		return this.http.get<any>(this.setEndPoint('integration/getExternalCustomers&channel=' + channel));
	}

	// ---- Channel Orders ----

	getData(filters: {
		datefrom?: string;
		dateto?: string;
		channel?: string;
		status?: string;
		search?: string;
		limit?: number;
		offset?: number;
	}): Observable<{ success: boolean; data: ChannelOrder[]; total: number }> {
		return this.http.post<any>(this.setEndPoint('channelorders/getData'), filters);
	}

	getDetail(orderno: string): Observable<any> {
		return this.http.get<any>(this.setEndPoint('channelorders/getDetail&orderno=' + encodeURIComponent(orderno)));
	}

	pushLeaflink(orderno: string): Observable<any> {
		return this.http.post(this.setEndPoint('channelorders/pushLeaflink'), { orderno });
	}

	pushDistru(orderno: string): Observable<any> {
		return this.http.post(this.setEndPoint('channelorders/pushDistru'), { orderno });
	}

	retryPush(orderno: string, channel: string): Observable<any> {
		return this.http.post(this.setEndPoint('channelorders/retryPush'), { orderno, channel });
	}

	pollInbound(channel: string): Observable<any> {
		return this.http.post(this.setEndPoint('channelorders/pollInbound'), { channel });
	}

	// ---- Reports ----

	getStats(datefrom: string, dateto: string): Observable<any> {
		return this.http.post<any>(this.setEndPoint('channelreports/getStats'), { datefrom, dateto });
	}

	getTrends(datefrom: string, dateto: string): Observable<any> {
		return this.http.post<any>(this.setEndPoint('channelreports/getTrends'), { datefrom, dateto });
	}

	getTopProducts(filters: { datefrom?: string; dateto?: string; channel?: string; limit?: number }): Observable<any> {
		return this.http.post<any>(this.setEndPoint('channelreports/getTopProducts'), filters);
	}

	getBrandBreakdown(filters: { datefrom?: string; dateto?: string; channel?: string }): Observable<any> {
		return this.http.post<any>(this.setEndPoint('channelreports/getBrandBreakdown'), filters);
	}
}
